import {createPortal} from "react-dom";
import {useState, useEffect, useRef, useCallback} from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  X,
  Maximize,
  Minimize,
  Lock,
  Loader2,
} from "lucide-react";

import {formatPlaybackTime} from "../../../utils/datetime";
import getProgressData, {
  isOrientation as isOrientationVideo,
} from "../utils/getProgressData";
import ensureYouTubeApi from "../utils/ensureYouTubeApi";
import getFurthestPosition from "../utils/getFurthestPosition";
import creditedSeconds from "../utils/creditedSeconds";

const isYouTubeUrl = (url) =>
  !!url && (url.includes("youtube.com") || url.includes("youtu.be"));

// Touch-primary device (phone/tablet). Used to predict when the YouTube
// MOBILE player will paint its own center play/pause (see the center-control
// render below); jsdom has no matchMedia, hence the guards.
const IS_COARSE_POINTER =
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(pointer: coarse)")?.matches;

// All iOS browsers share WebKit and its unmute-pauses-playback policy; the
// recovery net below is meaningless (and actively harmful) anywhere else.
const IS_IOS = /iP(hone|od|ad)/.test(
  (typeof navigator !== "undefined" && navigator.userAgent) || "",
);

const VideoModal = ({
  video,
  watchHistory,
  onClose,
  recentVideos,
  onProgress,
  onPlay, // Ensure this is called when the modal video starts playing
  playingId, // NEW: currently playing video id from parent
  onSwitchVideo, // NEW: swap modal to a different video (suggestion click)
  queue = [], // NEW: ordered playlist this video belongs to (up-next + autoplay)
}) => {
  const videoRef = useRef(null);
  const ytContainerRef = useRef(null);
  const playerRef = useRef(null);
  const hlsRef = useRef(null);
  // The element we put into the browser's native fullscreen and/or expand to
  // fill the viewport via CSS. Wraps the video/iframe + the controls overlay
  // so the controls stay reachable in fullscreen.
  const playerWrapRef = useRef(null);
  // There is deliberately NO queued-play path anymore. A tap that lands
  // before the player is ready cannot be honored later on iOS: by the time
  // the player signals ready the tap's transient activation has expired,
  // and a gesture-less play is blocked outright in Low Power Mode and
  // frozen by the unmute policy otherwise (two QA rounds of "Not Started
  // videos never play"). Instead the play control renders as a spinner
  // until the player is ready, so every tap that can happen is one that
  // works -- synchronously, with sound. (Auto-advance keeps its own
  // muted-autoplay path via wantsAutoplay.)

  // Round-3 #4 (player loops / balloons memory): the previous
  // implementation took `savedTime` from `watchHistory[video.id]` on every
  // render and passed it into the init `useEffect` dep array. Because the
  // parent calls `onProgress` ~once per second of playback, that updates
  // `watchHistory` -> `savedTime` changes -> the init effect re-runs ->
  // cleanupPlayers() destroys the player and rebuilds it from the new
  // savedTime -> the new player starts -> next second `savedTime` changes
  // again -> infinite re-init loop, growing memory each cycle.
  //
  // Fix: capture `savedTime`, `onProgress`, `onPlay` in refs. The init
  // effect now depends only on the *identity* of the video (id + url).
  // The live values are still read via the refs on every tick, so progress
  // reporting stays accurate.
  const initialSavedTimeRef = useRef(watchHistory[video.id]?.lastPosition || 0);
  const lastTimeRef = useRef(initialSavedTimeRef.current);
  // The furthest point the viewer has actually reached in the CURRENT video.
  // Orientation/intro videos may be rewound freely but not fast-forwarded
  // past this high-water mark, so the participant can't skip required content.
  // Seeded from saved progress and bumped on every playback tick.
  const maxReachedRef = useRef(initialSavedTimeRef.current);
  const onProgressRef = useRef(onProgress);
  const onPlayRef = useRef(onPlay);
  // When a play tap lands before the player is ready, the resulting play
  // call fires from an async ready/manifest callback -- i.e. OUTSIDE any
  // user gesture -- so the browser autoplay policy blocks unmuted
  // playback. (This is the intermittent "open a Not Started video and tap
  // right away -> it never plays" report: when the tap beats the player's
  // initialization, the queued play is gesture-less and gets blocked.)
  // We work around it by starting that deferred play MUTED (always
  // allowed) and unmuting the instant playback actually begins. This ref
  // tracks the in-flight muted-autoplay handshake so the volume-sync
  // effect doesn't fight it and so we know to unmute on the first PLAYING
  // signal. The synchronous (already-ready) tap path keeps its real user
  // gesture and is unaffected.
  const deferredMutedRef = useRef(false);
  // Timestamp of the deferred-path unMute we just issued. iOS WebKit
  // PAUSES a muted-autoplaying video that is unmuted without a live user
  // gesture; a PAUSED arriving within this window of our own unmute is
  // that policy firing, not the user.
  const unmuteAtRef = useRef(0);
  // The transient user-activation window (~1s) decides whether an unmute
  // is safe: if the tap that queued this play has expired, unmuting
  // freezes playback on iOS. Browsers without the API keep the legacy
  // always-unmute behavior.
  const gestureStillLive = () =>
    navigator.userActivation ? navigator.userActivation.isActive : true;
  // Live mirror of `isMuted` for the async player callbacks, which close
  // over a stale render's value.
  const isMutedRef = useRef(false);
  // Play/pause is INTENT-driven, not fire-and-forget. Two facts forced
  // this (Android QA 2026-08-07..10): (1) Android can silently drop an
  // unmuted playVideo() -- the call crosses into the iframe via
  // postMessage, the tap's gesture doesn't travel with it, and a blocked
  // play changes nothing, not even an error; (2) every command and every
  // state event is a postMessage round-trip late, so rapid taps race the
  // lag and a single dropped command left UI and player disagreeing
  // forever. Taps therefore record the user's last INTENT here, and
  // reconcilePlayIntent() drives the player toward it -- one in-flight
  // command at a time, re-checked after a round-trip, retrying muted once
  // for a policy-blocked play (sound returns on the first PLAYING under
  // the deferred-mute contract). Whatever the player or browser drops,
  // the loop converges on the last tap.
  const desiredPlayingRef = useRef(null); // null = no pending intent
  const reconcileTimerRef = useRef(null);
  const mutedRescueTriedRef = useRef(false);
  // How long the reconciler keeps working an intent before surfacing the
  // truth. Set per tap: long for plays, short for pauses.
  const intentDeadlineRef = useRef(0);
  // Pointer-level tap detection for the video surface: synthetic click on
  // Android is heuristic-laden (tap-slop cancellation while the URL bar
  // shows/hides resizes the viewport -- which fullscreen never has, hence
  // taps there always worked). pointerdown/up with a movement check is
  // immediate and heuristic-free; the synthetic click that follows is
  // deduped by timestamp.
  const surfacePointerRef = useRef(null); // {id, x, y} of the active touch
  const lastPointerToggleRef = useRef(0);
  // Auto-clear timer for the "Loading video…" pulse shown when a tap lands
  // before the player is ready (the tap itself stays un-queued).
  const loadingHintTimerRef = useRef(null);

  // Seconds left on the auto-advance countdown shown when a video ends and the
  // playlist has a next video. null = not counting down.
  const [countdown, setCountdown] = useState(null);

  // Set true right before an auto-advance / "Play now" switch so the next
  // video starts playing on its own once its player is ready, instead of
  // loading paused like a normal open. Read in the init effect below.
  const autoplayNextRef = useRef(false);

  // The id of the currently-loaded video. The YouTube onStateChange handler is
  // created once and reused across a loadVideoById swap, so it must read the
  // CURRENT id from here (not its stale closure) to log progress/ended for the
  // right video.
  const videoIdRef = useRef(video.id);
  // Set true right before a YouTube->YouTube auto-advance: keep the existing
  // (already user-activated) player and swap the video via loadVideoById,
  // instead of building a fresh iframe. A new iframe cannot autoplay on
  // iOS/WebKit (no user activation); reusing the player carries it forward.
  const reuseRef = useRef(false);

  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);
  useEffect(() => {
    onPlayRef.current = onPlay;
  }, [onPlay]);

  // Reset the captured savedTime whenever the modal is asked to show a
  // different video (id change). We do NOT capture the ever-changing
  // value during playback.
  useEffect(() => {
    initialSavedTimeRef.current = watchHistory[video.id]?.lastPosition || 0;
    lastTimeRef.current = initialSavedTimeRef.current;
    maxReachedRef.current = initialSavedTimeRef.current;
    videoIdRef.current = video.id;
    setCaptionTracks([]); // caption data is per-video; refetched on the new one
    setCaptionStatus("unknown");
    setCaptionNotice(null);
    setHasPlayedOnce(false);
    hasPlayedRef.current = false;
    captionVerifyTimersRef.current.forEach(clearTimeout);
    captionVerifyTimersRef.current = [];
    // Carry the viewer's caption choice onto the incoming video.
    pendingCaptionRef.current = captionsOnRef.current;
    setCountdown(null); // cancel any pending auto-advance from the prior video
    // Intentionally not including `watchHistory` -- see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  // True for a beat after a too-early play tap; renders the "Loading
  // video…" pulse beside the spinner so the tap has visible feedback.
  const [showLoadingHint, setShowLoadingHint] = useState(false);
  // A play intent is being worked by the reconciler: the center control
  // shows a buffering spinner (YouTube-style) instead of nothing, so a
  // slow player boot reads as loading, never as a dead tap.
  const [playPending, setPlayPending] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(initialSavedTimeRef.current);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Controls (progress bar + buttons) visibility. Auto-hides while playing
  // so fullscreen stays clean; any tap brings it back.
  const [controlsVisible, setControlsVisible] = useState(true);
  // The YouTube channel name. Our video records don't carry it, so we pull it
  // from YouTube's public oEmbed endpoint (author_name) when the modal opens.
  // Empty for non-YouTube videos or if the lookup fails.
  const [channel, setChannel] = useState("");

  // Playback speed. Applies to every video (YouTube + HTML5). The preference
  // is kept across video switches (see the sync effect) so a viewer who picks
  // 1.5x keeps it for the next clip.
  const PLAYBACK_RATES = [1, 1.5, 2];
  const [playbackRate, setPlaybackRate] = useState(1);
  const playbackRateRef = useRef(1);

  // Captions. Off by default; the CC button opens a language menu (Off /
  // English / Español / any other tracks the video carries). YouTube videos
  // are driven through the player's caption module on the LIVE player -- no
  // iframe rebuild -- because the cc_* player vars are only preferences
  // YouTube may ignore (Spanish silently fell back to English) and rebuilds
  // raced the module load (captions often never appeared).
  //   captionLang: { code: 'es', label: 'Español' }; null = default track.
  const [captionsOn, setCaptionsOn] = useState(false);
  const [captionTracks, setCaptionTracks] = useState([]);
  const [captionLang, setCaptionLang] = useState(null);
  const [showCaptionMenu, setShowCaptionMenu] = useState(false);
  // 'unknown' until the caption module reports in; 'ready' once the video
  // shows at least one track; 'none' when the video provably has no captions
  // (drives the "No captions available" menu state).
  const [captionStatus, setCaptionStatus] = useState("unknown");
  // Transient on-player notice ("No captions are available…") shown when the
  // no-captions verdict lands AFTER the viewer already picked a language.
  const [captionNotice, setCaptionNotice] = useState(null);
  // State twin of hasPlayedRef: re-arms the availability probe on first play
  // (the ref alone can't trigger the effect).
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  // Live mirrors for the async player callbacks.
  const captionsOnRef = useRef(false);
  const captionLangRef = useRef(null);
  // A selection made before the caption module was up; applied from
  // onApiChange / the probe timers, where the tracklist actually exists.
  const pendingCaptionRef = useRef(false);
  // Whether THIS video has reached PLAYING at least once. "No captions" is
  // only trustworthy after playback starts -- the tracklist stays empty on a
  // never-started player.
  const hasPlayedRef = useRef(false);
  // The caption helpers live below the player-init effect; the player's
  // event closures reach them through refs so they never go stale.
  const readCaptionTracksRef = useRef(null);
  const applyCaptionSelectionRef = useRef(null);
  // Pending verify-and-retry timers for a caption selection (see
  // applyCaptionSelection); cleared on every new selection / video switch.
  const captionVerifyTimersRef = useRef([]);

  const isYouTube =
    video.url.includes("youtube.com") || video.url.includes("youtu.be");

  // Orientation/intro videos are a program's required first watch, so they
  // can't be fast-forwarded. Shared predicate (getProgressData) so the seek
  // gate, the 80% completion rule, and the badge all flip together — it
  // covers the SNAP and TANF orientation videos plus any explicit is_intro.
  const isOrientation = isOrientationVideo(video);

  // We can reuse the live player (loadVideoById) only when the next video is
  // YouTube and a YouTube player already exists (its presence implies the
  // current video is YouTube too). Uses only refs + a pure helper, so callers
  // in deps-light effects don't go stale. This is the only path that keeps
  // autoplay working on iOS.
  const canReuse = (next) =>
    isYouTubeUrl(next?.url) && !!playerRef.current?.loadVideoById;
  const canReuseRef = useRef(canReuse);
  canReuseRef.current = canReuse;

  // Look up the YouTube channel name from the public oEmbed endpoint whenever
  // the loaded video changes. oEmbed is CORS-enabled and needs no auth, and
  // (unlike the player's getVideoData) returns author_name without the video
  // having to start playing. `cancelled` guards against a stale response
  // landing after the modal has switched videos.
  useEffect(() => {
    setChannel("");
    if (!isYouTube) return;
    let cancelled = false;
    fetch(
      `https://www.youtube.com/oembed?url=${encodeURIComponent(
        video.url,
      )}&format=json`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.author_name) setChannel(d.author_name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [video.url, isYouTube]);

  // Live label/classes/dot reflect the furthest position reached; read off
  // `watchHistory` directly so the progress chip stays accurate without
  // re-running the init effect. Furthest (not the resume position) so a
  // rewatch can't flip a Completed/Watched chip back to In Progress.
  const liveSavedTime = getFurthestPosition(watchHistory[video.id]);
  const {label, classes, dot} = getProgressData(liveSavedTime, video);

  // "Up next" is the rest of the current playlist after this video. When the
  // modal was opened outside a playlist (search/grid), `queue` is empty and we
  // fall back to the recently-watched suggestions so the panel isn't empty.
  const idxInQueue = queue.findIndex((v) => v?.id === video.id);
  const upNext =
    idxInQueue >= 0
      ? queue
          .slice(idxInQueue + 1)
          .filter((v) => v && v.url && v.id !== video.id)
      : [];
  const nextVideo = upNext[0] || null;
  const fromQueue = upNext.length > 0;
  // The whole rest of the playlist (or the recently-watched fallback). The
  // list scrolls and its thumbnails lazy-load, so a long playlist is fine.
  const suggestions = fromQueue
    ? upNext
    : recentVideos.filter((v) => v.id !== video.id && v.url);

  // The end-of-video handlers live inside the init effect's player callbacks,
  // which close over the first render. Read the live "next" through a ref and
  // expose a stable starter the player callbacks can call on ENDED.
  const nextVideoRef = useRef(null);
  useEffect(() => {
    nextVideoRef.current = nextVideo;
  }, [nextVideo]);
  const beginCountdown = useCallback(() => {
    if (nextVideoRef.current) setCountdown(5);
  }, []);
  const beginCountdownRef = useRef(beginCountdown);
  useEffect(() => {
    beginCountdownRef.current = beginCountdown;
  }, [beginCountdown]);

  // Read onSwitchVideo through a ref so the tick effect below depends ONLY on
  // `countdown`. The parent passes a fresh onSwitchVideo identity every render,
  // and the activity heartbeat re-renders the dashboard every second -- if it
  // were a dep, each re-render would clear and reset the 1s timer before it
  // fired, freezing the countdown at its starting value.
  const onSwitchVideoRef = useRef(onSwitchVideo);
  useEffect(() => {
    onSwitchVideoRef.current = onSwitchVideo;
  }, [onSwitchVideo]);

  // Tick the auto-advance countdown once per second; at 0, switch to the next
  // video in the playlist.
  useEffect(() => {
    if (countdown == null) return undefined;
    if (countdown <= 0) {
      const next = nextVideoRef.current;
      setCountdown(null);
      if (next && onSwitchVideoRef.current) {
        autoplayNextRef.current = true;
        if (canReuseRef.current(next)) reuseRef.current = true;
        onSwitchVideoRef.current(next);
      }
      return undefined;
    }
    const t = setTimeout(
      () => setCountdown((c) => (c == null ? null : c - 1)),
      1000,
    );
    return () => clearTimeout(t);
  }, [countdown]);

  // --- 1. INITIALIZATION EFFECT ---
  // Round-3 #4: deps are limited to the *identity* of the loaded video
  // (id + url + youtube branch). `savedTime` is read from a ref captured
  // when the modal opens or the video switches, NOT from live
  // watchHistory -- otherwise every onProgress tick would tear down and
  // rebuild the player, looping playback and leaking memory.
  useEffect(() => {
    let interval;
    // Cancellation for the deferred YouTube init below: without it, a
    // video switch / unmount during the "waiting for window.YT" retry loop
    // leaves an orphaned chain that later builds a zombie player into the
    // (by then reused or detached) container.
    let ytCancelled = false;
    let ytRetryTimer;
    const startAt = initialSavedTimeRef.current;
    // Carried-over intent from an auto-advance / "Play now": the next video
    // should start on its own. Mobile browsers block a gesture-less
    // programmatic play once the tap's transient activation has expired (after
    // the 5s countdown), so instead we let the player AUTOPLAY MUTED at load --
    // the one form of autoplay mobile allows -- and unmute once it actually
    // starts. Consumed here; a video switch is an update so this runs once.
    const wantsAutoplay = autoplayNextRef.current;
    autoplayNextRef.current = false;
    setIsPlayerReady(false);
    setIsPlaying(false);
    // A pending intent belongs to the previous video.
    desiredPlayingRef.current = null;
    mutedRescueTriedRef.current = false;
    setPlayPending(false);
    setCurrentTime(startAt);
    lastTimeRef.current = startAt;

    const cleanupPlayers = () => {
      if (interval) clearInterval(interval);
      // On a YouTube->YouTube auto-advance we keep the player alive and swap
      // the video via loadVideoById, so its iframe stays user-activated --
      // the only way iOS/WebKit keeps autoplaying. Don't tear it down then.
      if (reuseRef.current) return;
      if (playerRef.current?.destroy) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }
    };

    // Effect cleanup: also stop the deferred-init retry chain. Kept separate
    // from cleanupPlayers, which loadYT reuses as an inner pre-create
    // teardown where cancelling would be wrong.
    const teardown = () => {
      ytCancelled = true;
      if (ytRetryTimer) clearTimeout(ytRetryTimer);
      if (reconcileTimerRef.current) {
        clearTimeout(reconcileTimerRef.current);
        reconcileTimerRef.current = null;
      }
      if (loadingHintTimerRef.current) {
        clearTimeout(loadingHintTimerRef.current);
        loadingHintTimerRef.current = null;
      }
      cleanupPlayers();
    };

    if (isYouTube) {
      const videoId = video.url.includes("v=")
        ? video.url.split("v=")[1].split("&")[0]
        : video.url.split("/").pop().split("?")[0];

      // Per-second progress poll. Reads the *current* id via the ref so a
      // reused player (after loadVideoById) logs against the right video.
      const startProgressInterval = () =>
        setInterval(() => {
          const p = playerRef.current;
          if (
            p?.getCurrentTime &&
            p?.getPlayerState &&
            p.getPlayerState() === window.YT.PlayerState.PLAYING
          ) {
            const curr = p.getCurrentTime();
            setCurrentTime(curr);
            if (curr > maxReachedRef.current) maxReachedRef.current = curr;
            const dur = p.getDuration?.();
            if (dur) setDuration(dur);
            const delta = curr - lastTimeRef.current;
            const credit = creditedSeconds(delta, p.getPlaybackRate?.(), 2);
            if (credit > 0)
              onProgressRef.current?.(videoIdRef.current, curr, credit);
            lastTimeRef.current = curr;
          }
        }, 1000);

      // REUSE PATH: swap the video into the existing, already user-activated
      // player. The prior effect's cleanup left it alive (reuseRef). This is
      // what lets the next video keep autoplaying on iOS.
      const reuse = reuseRef.current && !!playerRef.current?.loadVideoById;
      reuseRef.current = false;
      if (reuse) {
        try {
          playerRef.current.loadVideoById({
            videoId,
            startSeconds: startAt > 0 ? startAt : 0,
          });
        } catch (e) {}
        setIsPlayerReady(true);
        interval = startProgressInterval();
        return teardown;
      }

      // The modal cannot assume the YouTube IFrame API is already on the
      // page. It used to only poll `window.YT` and wait, which meant that
      // on a fresh page load -- before any inline VideoYoutubePlayer had
      // mounted and injected the script -- `window.YT` stayed undefined
      // forever, `onReady` never fired, and the play tap (queued or not)
      // was silently dropped. This was the "refresh, expand a Not Started
      // video before playing -> never plays" repro. Inject the script here
      // too (idempotently) so the modal is self-sufficient.
      ensureYouTubeApi();

      const loadYT = () => {
        if (ytCancelled) return;
        if (!window.YT || !window.YT.Player) {
          ytRetryTimer = setTimeout(loadYT, 100);
          return;
        }

        cleanupPlayers();

        playerRef.current = new window.YT.Player(ytContainerRef.current, {
          height: "100%",
          width: "100%",
          videoId: videoId,
          playerVars: {
            // Autoplay (muted) the next video on auto-advance -- mobile blocks
            // unmuted gesture-less playback. playsinline keeps iOS from forcing
            // fullscreen on play (which needs a gesture and would block
            // autoplay). The generated iframe already carries allow="autoplay".
            autoplay: wantsAutoplay ? 1 : 0,
            mute: wantsAutoplay ? 1 : 0,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
            controls: 0,
            disablekb: 1,
            enablejsapi: 1,
            origin: window.location.origin,
          },
          events: {
            onReady: (e) => {
              const ytDuration = e.target.getDuration();
              setDuration(ytDuration);
              if (startAt > 0) e.target.seekTo(startAt, true);
              if (wantsAutoplay) {
                // YouTube autoplays via the muted player vars above. Flag the
                // handshake so sound is restored on the first PLAYING (where
                // the browser permits it); set before isPlayerReady so the
                // volume-sync effect doesn't fight it.
                if (!isMutedRef.current) deferredMutedRef.current = true;
                onPlayRef.current?.(video.id);
              }
              setIsPlayerReady(true);
              // Captions already on (carried from the previous video or
              // chosen before ready): bring the module up on this player.
              // The selection itself lands via onApiChange / the probes.
              if (captionsOnRef.current) {
                pendingCaptionRef.current = true;
                try {
                  e.target.loadModule("captions");
                } catch (err) {}
              }
            },
            onStateChange: (event) => {
              // This handler is created once and survives a loadVideoById
              // reuse, so it must read the CURRENT id via the ref.
              if (event.data === window.YT.PlayerState.PLAYING) {
                logTapRef.current?.("YT:PLAYING");
                // With an intent pending, the reconciler owns isPlaying
                // (it may need to issue a follow-up command if the user
                // flipped again mid-flight); with none, follow the player.
                if (desiredPlayingRef.current === null) setIsPlaying(true);
                else reconcileRef.current?.();
                hasPlayedRef.current = true;
                setHasPlayedOnce(true); // re-arms the caption probe
                onPlayRef.current?.(videoIdRef.current);
                // The muted-autoplay handshake succeeded. Restore sound
                // only while the user's tap is still a live gesture:
                // unmuting after it expires makes iOS pause the video --
                // the "Not Started video freezes on its first frame" QA
                // bug. Otherwise stay muted and tell the UI, so the
                // speaker icon is truthful and one real tap restores sound.
                if (deferredMutedRef.current) {
                  deferredMutedRef.current = false;
                  if (!isMutedRef.current) {
                    if (gestureStillLive()) {
                      unmuteAtRef.current = Date.now();
                      event.target.unMute();
                    } else {
                      setIsMuted(true);
                    }
                  }
                }
              } else if (event.data === window.YT.PlayerState.PAUSED) {
                // Recovery net: a PAUSED right after our own unmute is the
                // iOS mute policy stopping playback, not the user. Re-mute,
                // reflect it in the UI, and resume. STRICTLY GATED: iOS
                // only (the policy exists nowhere else -- on Android every
                // pause it caught was a REAL user pause, and auto-replaying
                // against the user's finger was the "dead for a couple
                // seconds" window, QA 08-10 r9), and never when a user
                // pause intent is pending, on any platform.
                if (
                  IS_IOS &&
                  desiredPlayingRef.current !== false &&
                  unmuteAtRef.current &&
                  Date.now() - unmuteAtRef.current < 800
                ) {
                  unmuteAtRef.current = 0;
                  event.target.mute();
                  setIsMuted(true);
                  event.target.playVideo();
                  return;
                }
                logTapRef.current?.("YT:PAUSED");
                if (desiredPlayingRef.current === null) setIsPlaying(false);
                else reconcileRef.current?.();
              } else if (event.data === window.YT.PlayerState.ENDED) {
                logTapRef.current?.("YT:ENDED");
                desiredPlayingRef.current = null;
                setPlayPending(false);
                setIsPlaying(false);
                onProgressRef.current?.(
                  videoIdRef.current,
                  event.target.getDuration(),
                  0,
                );
                beginCountdownRef.current?.();
              }
            },
            onApiChange: (event) => {
              // The caption module just came up -- this is the one reliable
              // moment its tracklist is actually populated.
              const list = readCaptionTracksRef.current?.(event.target) || [];
              if (pendingCaptionRef.current) {
                applyCaptionSelectionRef.current?.();
              }
              // Fast no-captions verdict: the module is up, the video has
              // played, and there are no tracks. One short re-check guards
              // against a list that populates a beat after the event.
              if (!list.length && hasPlayedRef.current) {
                setTimeout(() => {
                  const again =
                    readCaptionTracksRef.current?.(playerRef.current) || [];
                  if (!again.length) setCaptionStatus("none");
                }, 400);
              }
            },
          },
        });

        // Start the poll only AFTER the player exists. The old code started
        // it right after calling loadYT(); when player creation was deferred
        // (fresh page load, window.YT not yet available), the retry's
        // cleanupPlayers() above then cleared that already-running interval
        // and nothing restarted it -- video played, but the clock stayed at
        // 0:00 and onProgress (watch-time credit) never fired.
        interval = startProgressInterval();
      };

      loadYT();
    } else {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      cleanupPlayers();

      // Deferred (gesture-less) HTML5 play: start muted so the autoplay
      // policy allows it, then unmute once playback begins (unless the
      // user explicitly muted). Mirrors the YouTube handshake above.
      const playDeferred = (el) => {
        if (!isMutedRef.current) {
          deferredMutedRef.current = true;
          el.muted = true;
        }
        el.play()
          .then(() => {
            if (deferredMutedRef.current) {
              deferredMutedRef.current = false;
              // Same iOS rule as the YouTube path: only unmute while the
              // gesture is live, else keep playing muted and say so.
              if (!isMutedRef.current) {
                if (gestureStillLive()) el.muted = false;
                else setIsMuted(true);
              }
            }
          })
          .catch(() => {
            deferredMutedRef.current = false;
          });
      };

      if (
        video.url.includes(".m3u8") &&
        window.Hls &&
        window.Hls.isSupported()
      ) {
        const hls = new window.Hls();
        hls.loadSource(video.url);
        hls.attachMedia(videoElement);
        hlsRef.current = hls;

        hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
          if (startAt > 0) videoElement.currentTime = startAt;
          setIsPlayerReady(true);
          if (wantsAutoplay) {
            onPlayRef.current?.(video.id);
            playDeferred(videoElement);
          }
        });
      } else {
        videoElement.src = video.url;
        if (startAt > 0) videoElement.currentTime = startAt;
        setIsPlayerReady(true);
        if (wantsAutoplay) {
          onPlayRef.current?.(video.id);
          playDeferred(videoElement);
        }
      }
    }

    return teardown;
    // `onProgress` and `onPlay` are read via refs; `initialSavedTimeRef`
    // is reset via the dedicated [video.id] effect above. Excluding them
    // here is intentional -- see Round-3 #4 comment. Captions never rebuild
    // the player; they're applied to the live player in section 3c.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id, video.url, isYouTube]);

  // --- 2. PAUSE WHEN ANOTHER VIDEO STARTS PLAYING ---
  useEffect(() => {
    if (!isPlayerReady) return;
    if (!playingId || playingId === video.id) return;

    if (isYouTube && playerRef.current?.pauseVideo) {
      playerRef.current.pauseVideo();
    } else if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }

    setIsPlaying(false);
  }, [playingId, video.id, isYouTube, isPlayerReady]);

  // --- 3. VOLUME SYNC ---
  useEffect(() => {
    isMutedRef.current = isMuted;
    if (!isPlayerReady) return;
    // Don't fight the in-flight muted-autoplay handshake; it will restore
    // sound itself once playback begins.
    if (deferredMutedRef.current) return;

    if (isYouTube && playerRef.current?.mute) {
      isMuted ? playerRef.current.mute() : playerRef.current.unMute();
    } else if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, isPlayerReady, isYouTube]);

  // --- 3b. PLAYBACK-SPEED SYNC ---
  // Runs on rate change AND whenever the player becomes ready. A video switch
  // flips isPlayerReady false->true, so the chosen rate is re-applied to the
  // new (or reused) player without the viewer having to reselect it.
  useEffect(() => {
    playbackRateRef.current = playbackRate;
    if (!isPlayerReady) return;
    if (isYouTube && playerRef.current?.setPlaybackRate) {
      try {
        playerRef.current.setPlaybackRate(playbackRate);
      } catch (e) {}
    } else if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, isPlayerReady, isYouTube]);

  // --- 3c. CAPTIONS ---
  // YouTube captions are controlled at runtime through the player's caption
  // module (loadModule / setOption) on the LIVE player -- never by rebuilding
  // the iframe. The previous cc_load_policy / cc_lang_pref rebuild approach
  // was flaky: those vars are only *preferences* (Spanish silently fell back
  // to English) and the rebuild raced the module load (captions often never
  // appeared at all).
  //
  // The tracklist is only populated once the module is up, which the player
  // signals via onApiChange; selections made before that are parked in
  // pendingCaptionRef and applied there (probe timers below are the fallback
  // for players that never fire onApiChange). A picked language uses the
  // video's own track when one exists and YouTube's auto-translation of the
  // first track otherwise. Videos with no tracks at all surface as
  // "No captions available" once they have actually played.

  const baseLangOf = (code) => (code || "").toLowerCase().split(/[-_]/)[0];

  // Read the live player's tracklist into state; returns the raw list.
  // State writes are skipped while the list is still empty -- the
  // availability poll below calls this every 250ms, and a fresh [] on every
  // miss would re-render the modal for nothing (state starts as [] and is
  // reset on video change).
  const readCaptionTracks = (p) => {
    if (!p?.getOption) return [];
    let list = [];
    try {
      list = p.getOption("captions", "tracklist") || [];
    } catch (e) {
      return [];
    }
    const tracks = list
      .filter((t) => t && t.languageCode)
      .map((t) => ({
        code: t.languageCode,
        label: t.displayName || t.languageName || t.languageCode,
      }));
    if (tracks.length) {
      setCaptionTracks(tracks);
      setCaptionStatus("ready");
    }
    return list;
  };
  readCaptionTracksRef.current = readCaptionTracks;

  // Push the current on/off + language choice into the player. Safe to call
  // any time; parks itself in pendingCaptionRef while the module is still
  // coming up. Reads everything through refs so stale closures are harmless.
  //
  // Reliability: the player sometimes ACCEPTS a setOption yet keeps showing
  // the previous/default track (the "picked Spanish, still English" bug),
  // especially right after the module loads. So every selection is written
  // with the combined form (track + translationLanguage in ONE call, which
  // sticks far better than two sequential calls) and then VERIFIED shortly
  // after by reading the applied track back -- re-applying if it didn't take.
  const applyCaptionSelection = () => {
    const p = playerRef.current;
    if (!p?.getOption) return;
    captionVerifyTimersRef.current.forEach(clearTimeout);
    captionVerifyTimersRef.current = [];
    const on = captionsOnRef.current;
    const lang = captionLangRef.current;
    try {
      if (!on) {
        pendingCaptionRef.current = false;
        try {
          p.setOption("captions", "track", {});
        } catch (e) {}
        try {
          p.unloadModule("captions");
        } catch (e) {}
        return;
      }
      p.loadModule("captions");
      const list = p.getOption("captions", "tracklist") || [];
      if (!list.length) {
        pendingCaptionRef.current = true; // applied on onApiChange / probes
        return;
      }
      pendingCaptionRef.current = false;
      const want = lang?.code ? baseLangOf(lang.code) : null;
      // Videos can carry several tracks with the SAME language code (e.g. a
      // manual ".en" plus an auto-generated ASR "a.en"). A bare
      // {languageCode} setOption is then ambiguous -- the player may resolve
      // it to the ASR track and silently drop a requested translation (the
      // "IT Support Specialist stays English" bug). So selections always
      // pass the FULL track object from the tracklist, preferring the
      // manual track over ASR.
      const preferManual = (a, b) => (a.kind === "asr") - (b.kind === "asr");
      const matches = want
        ? list
            .filter((t) => baseLangOf(t.languageCode) === want)
            .sort(preferManual)
        : [];
      const exact = matches[0] || null;
      const target = exact || list.slice().sort(preferManual)[0];
      const translate = !!want && !exact;

      const write = () => {
        try {
          if (translate) {
            // No native track in that language: auto-translate `target`.
            p.setOption("captions", "track", {
              ...target,
              translationLanguage: {languageCode: lang.code},
            });
            // Older player builds honor only the standalone call; harmless
            // duplicate on current ones.
            p.setOption("captions", "translationLanguage", {
              languageCode: lang.code,
            });
          } else {
            // Native track (or the video's default). Clear any leftover
            // auto-translation from a previous choice.
            try {
              p.setOption("captions", "translationLanguage", null);
            } catch (e) {}
            p.setOption("captions", "track", {...target});
          }
        } catch (e) {}
      };
      write();

      const verify = () => {
        try {
          const cur = p.getOption("captions", "track") || {};
          const curBase = baseLangOf(cur.languageCode);
          const curTrans = baseLangOf(cur.translationLanguage?.languageCode);
          const ok = translate
            ? curTrans === want
            : curBase === baseLangOf(target.languageCode) &&
              !cur.translationLanguage;
          if (!ok) write();
        } catch (e) {}
      };
      [400, 1400].forEach((ms) => {
        captionVerifyTimersRef.current.push(setTimeout(verify, ms));
      });
    } catch (e) {}
  };
  applyCaptionSelectionRef.current = applyCaptionSelection;

  // Re-apply whenever the choice changes or a (new) player becomes ready.
  useEffect(() => {
    captionsOnRef.current = captionsOn;
    captionLangRef.current = captionLang;

    if (!isYouTube) {
      // HTML5 <video>: toggle native <track> text tracks in place, and let
      // the menu know what exists (none at all => "No captions available").
      const el = videoRef.current;
      if (!el) return;
      const tracks = el.textTracks || [];
      if (captionsOn) setCaptionStatus(tracks.length ? "ready" : "none");
      const want = captionLang?.code ? baseLangOf(captionLang.code) : null;
      let shown = false;
      for (let i = 0; i < tracks.length; i++) {
        const show =
          captionsOn &&
          !shown &&
          (!want || baseLangOf(tracks[i].language) === want);
        if (show) shown = true;
        tracks[i].mode = show ? "showing" : "disabled";
      }
      return;
    }
    if (!isPlayerReady || !playerRef.current) return;
    applyCaptionSelectionRef.current();
    // applyCaptionSelection reads everything through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captionsOn, captionLang, isYouTube, isPlayerReady]);

  // Availability probe. Runs as soon as the player is ready and re-arms when
  // playback first starts (YouTube only populates the tracklist after play),
  // NOT just when captions are turned on -- so by the time the viewer opens
  // the CC menu, a caption-less video already reads "No captions available"
  // instead of finding out only after a failed language pick. When captions
  // are off, the module is brought up silently (empty track) just to read
  // the list, then put away again.
  useEffect(() => {
    if (!isYouTube || !isPlayerReady) return undefined;
    if (captionStatus !== "unknown" && !pendingCaptionRef.current)
      return undefined;
    const p = playerRef.current;
    if (p?.getOption && !captionsOnRef.current) {
      try {
        p.loadModule("captions");
        p.setOption("captions", "track", {}); // module up, nothing displayed
      } catch (e) {}
    }
    const probe = (last) => {
      const list = readCaptionTracksRef.current?.(playerRef.current) || [];
      if (list.length) {
        if (pendingCaptionRef.current) {
          applyCaptionSelectionRef.current();
        } else if (!captionsOnRef.current) {
          // Probe-only visit -- put the module away again.
          try {
            playerRef.current?.unloadModule("captions");
          } catch (e) {}
        }
        return true;
      }
      if (last && hasPlayedRef.current) setCaptionStatus("none");
      return false;
    };
    // Poll fast, verdict slow: getOption is a cheap sync read, so a 250ms
    // cadence latches a pending pick almost as soon as the tracklist exists
    // (the old 700/1600/3200/5200 ladder could sit up to ~2s past it). The
    // 5.2s cutoff for the "none" verdict stays put -- slow connections
    // legitimately take seconds to surface the tracklist, and a false
    // "No captions available" is worse than a late one.
    const STEP_MS = 250;
    const DEADLINE_MS = 5200;
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += STEP_MS;
      const last = elapsed >= DEADLINE_MS;
      if (probe(last) || last) clearInterval(interval);
    }, STEP_MS);
    return () => clearInterval(interval);
    // Probes read live state through refs; captionStatus is guarded above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isYouTube,
    isPlayerReady,
    hasPlayedOnce,
    captionsOn,
    captionLang,
    video.id,
    captionStatus,
  ]);

  // Surface "no captions" the moment it's determined if the viewer had
  // already asked for them -- never make them reopen the menu to find out.
  useEffect(() => {
    if (captionStatus === "none" && captionsOnRef.current) {
      setCaptionNotice("No captions are available for this video.");
      const t = setTimeout(() => setCaptionNotice(null), 4000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [captionStatus]);

  // --- 4. FULLSCREEN ---
  // The CSS overlay (fixed inset-0) is the baseline and works everywhere,
  // including iOS Safari where the native Fullscreen API isn't available on
  // arbitrary elements / iframes. Where the native API IS available (Android
  // Chrome, desktop) we also request it, which additionally hides the browser
  // chrome. On iOS with a YouTube embed the OS URL bar can't be hidden by web
  // code -- that's a platform limitation we accept rather than work around.
  // Native exit (ESC / swipe) is synced back via the fullscreenchange listener.
  // lock=false is for the auto-rotate path: locking there would pin Android
  // to landscape and the rotate-back-to-portrait exit could never fire.
  const enterFullscreen = useCallback(({lock = true} = {}) => {
    setIsFullscreen(true);
    const el = playerWrapRef.current;
    const req = el?.requestFullscreen || el?.webkitRequestFullscreen;
    if (req) {
      try {
        const p = req.call(el);
        if (p?.catch) p.catch(() => {});
      } catch (e) {}
    }
    // Best-effort landscape lock; unsupported / rejected silently.
    if (lock && window.screen?.orientation?.lock) {
      window.screen.orientation.lock("landscape").catch(() => {});
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false);
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) {
        try {
          const p = exit.call(document);
          if (p?.catch) p.catch(() => {});
        } catch (e) {}
      }
    }
    if (window.screen?.orientation?.unlock) {
      try {
        window.screen.orientation.unlock();
      } catch (e) {}
    }
  }, []);

  const toggleFullscreen = (e) => {
    e?.stopPropagation();
    if (isFullscreen) exitFullscreen();
    else enterFullscreen();
  };

  // Sync our state when the user exits native fullscreen via ESC, the system
  // back gesture, or the browser's own control.
  useEffect(() => {
    const onChange = () => {
      const fsEl =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (!fsEl) setIsFullscreen(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // Make sure we leave native fullscreen / release the orientation lock if the
  // modal closes or switches videos while fullscreen is active.
  useEffect(
    () => () => {
      const fsEl =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (fsEl) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) {
          try {
            const p = exit.call(document);
            if (p?.catch) p.catch(() => {});
          } catch (e) {}
        }
      }
      if (window.screen?.orientation?.unlock) {
        try {
          window.screen.orientation.unlock();
        } catch (e) {}
      }
    },
    [],
  );

  // Auto-fullscreen on rotation: a sideways phone is a clear "make it big"
  // signal, and the portrait sheet is unusable in landscape anyway (the video
  // fills the viewport and the scrubber/CC/speed controls end up below the
  // fold with no way to scroll). Rotating back to portrait exits. Touch
  // devices only, so resizing a desktop window doesn't hijack the layout.
  // Also runs once on open, for videos opened with the phone already
  // sideways. requestFullscreen may be rejected here (a rotation isn't a
  // user gesture) -- fine, the CSS overlay is the actual mechanism.
  useEffect(() => {
    // Also absent in jsdom, so tests exercise the modal without this effect.
    if (typeof window.matchMedia !== "function") return undefined;
    if (!window.matchMedia("(pointer: coarse)").matches) return undefined;
    // max-height keeps this to phones: an iPad held landscape shouldn't
    // force every video fullscreen.
    const mq = window.matchMedia(
      "(orientation: landscape) and (max-height: 500px)",
    );
    const onChange = (e) => {
      if (e.matches) enterFullscreen({lock: false});
      else exitFullscreen();
    };
    if (mq.matches) enterFullscreen({lock: false});
    // Older iOS Safari only has the addListener form.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [enterFullscreen, exitFullscreen]);

  // Auto-hide the controls 3s after the last tap while playing. Any tap
  // sets controlsVisible back to true (see handleTogglePlay/handleSeek),
  // which restarts this timer. While paused, controls always stay visible.
  useEffect(() => {
    // Keep the controls up while the caption menu is open, otherwise it would
    // vanish mid-selection.
    if (isPlaying && controlsVisible && !showCaptionMenu) {
      const t = setTimeout(() => setControlsVisible(false), 3000);
      return () => clearTimeout(t);
    }
    if (!isPlaying) setControlsVisible(true);
  }, [isPlaying, controlsVisible, showCaptionMenu]);

  // Tap diagnostics, off unless the URL carries ?tapdebug (sticky via
  // localStorage; tapdebug=0 turns it off). Renders a tiny event log over
  // the player so a device test can show WHERE a dead tap died: no line =
  // the browser never delivered the tap; tap line without cmd = our
  // handler refused; cmd without a YT event = the player ignored it.
  const [tapDebugOn] = useState(() => {
    try {
      const q = window.location.search;
      if (q.includes("tapdebug=0")) {
        localStorage.removeItem("tapdebug");
        return false;
      }
      if (q.includes("tapdebug")) {
        localStorage.setItem("tapdebug", "1");
        return true;
      }
      return localStorage.getItem("tapdebug") === "1";
    } catch (e) {
      return false;
    }
  });
  const [tapLog, setTapLog] = useState([]);
  const logTap = (msg) => {
    if (!tapDebugOn) return;
    setTapLog((l) => [...l.slice(-6), `${Date.now() % 60000} ${msg}`]);
  };
  // The YT event handlers are created once per player and must see the
  // CURRENT logger/reconciler, not the closure from that render.
  const logTapRef = useRef(null);
  logTapRef.current = logTap;
  const reconcileRef = useRef(null);

  // Returns null when the wrapper THROWS -- its embed document can still
  // be booting (emulator forensics 08-10: a cold YouTube embed took ~35s
  // to become functional). Every caller must treat null as "not there
  // yet", never let the throw escape into an event handler.
  const actualPlaying = () => {
    try {
      if (isYouTube) {
        const YTPS = window.YT?.PlayerState;
        const s = playerRef.current?.getPlayerState?.();
        return !!YTPS && (s === YTPS.PLAYING || s === YTPS.BUFFERING);
      }
      const el = videoRef.current;
      return !!el && !el.paused && !el.ended;
    } catch (err) {
      return null;
    }
  };

  // Drive the player toward desiredPlayingRef (see that ref's comment).
  // YouTube-parity contract: a play intent NEVER dies silently -- the
  // player boots on its own schedule on mobile (onReady can precede its
  // media pipeline actually accepting commands by seconds on a cold
  // start), so we show a buffering spinner and keep re-issuing the
  // command every cycle until the deadline. The old two-attempts-then-
  // give-up-at-800ms behavior read as "works half the time": fast boots
  // made it, slow boots didn't. Muted escalation after the first missed
  // cycle stays (sound restored on first PLAYING per the deferred-mute
  // contract), and only a blown deadline surfaces the player's truth.
  const reconcilePlayIntent = () => {
    if (reconcileTimerRef.current) {
      clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = null;
    }
    const desired = desiredPlayingRef.current;
    if (desired === null) return;
    // null = the wrapper threw (embed still booting; one throw in cycle 2
    // once killed the whole timer chain, so the intent looked permanently
    // dead -- Marco's "works half the time" was this race). actualPlaying
    // swallows the throw itself so no caller can die on it.
    const actual = actualPlaying();
    if (actual === desired) {
      desiredPlayingRef.current = null;
      mutedRescueTriedRef.current = false;
      setPlayPending(false);
      return;
    }
    if (Date.now() > intentDeadlineRef.current) {
      desiredPlayingRef.current = null;
      mutedRescueTriedRef.current = false;
      setPlayPending(false);
      setIsPlaying(actual === null ? false : actual);
      logTap("giveup");
      return;
    }
    // Schedule the next cycle BEFORE touching the player: nothing a
    // throwing wrapper does can kill the loop -- only match or deadline
    // ends it.
    reconcileTimerRef.current = setTimeout(() => {
      reconcileTimerRef.current = null;
      if (desiredPlayingRef.current) mutedRescueTriedRef.current = true;
      reconcilePlayIntent();
    }, 500);
    try {
      // A COLD unmuted play on a touch device is the known-doomed case on
      // Android (silently dropped). Start it muted up front -- always
      // allowed, sound restored on the first PLAYING while the tap's
      // gesture is still live. Never applies over the user's own mute.
      // The same escalation covers a WARM play whose first cycle missed.
      if (
        desired &&
        !isMutedRef.current &&
        !deferredMutedRef.current &&
        ((IS_COARSE_POINTER && !hasPlayedRef.current) ||
          mutedRescueTriedRef.current)
      ) {
        deferredMutedRef.current = true;
        if (isYouTube) playerRef.current?.mute?.();
        else if (videoRef.current) videoRef.current.muted = true;
        logTap("muted-start");
      }
      logTap(desired ? "cmd:play" : "cmd:pause");
      if (isYouTube && playerRef.current) {
        if (desired) playerRef.current.playVideo?.();
        else playerRef.current.pauseVideo?.();
      } else if (videoRef.current) {
        if (desired) videoRef.current.play?.()?.catch?.(() => {});
        else videoRef.current.pause?.();
      }
    } catch (err) {
      logTap("cmd-threw");
    }
  };
  reconcileRef.current = reconcilePlayIntent;

  const handleTogglePlay = (e) => {
    e?.stopPropagation();
    setControlsVisible(true);
    if (!isPlayerReady) {
      // Not ready = no playable surface exists yet, and a play queued here
      // could not be honored later (see the no-queued-play note up top).
      // The tap still gets visible feedback so it doesn't read as dead.
      setShowLoadingHint(true);
      if (loadingHintTimerRef.current)
        clearTimeout(loadingHintTimerRef.current);
      loadingHintTimerRef.current = setTimeout(
        () => setShowLoadingHint(false),
        1500,
      );
      return;
    }
    // Flip from the pending intent when one exists, else from the player's
    // ACTUAL state -- never from React's isPlaying, which lags the player
    // by a round-trip and made a quick play tap re-run the pause branch.
    // A booting wrapper (actualPlaying() === null) counts as not playing,
    // so the tap becomes a play intent for the reconciler instead of dying.
    const next = !(desiredPlayingRef.current ?? actualPlaying() ?? false);
    desiredPlayingRef.current = next;
    // Plays get a long leash -- a cold YouTube embed can take 15s+ to boot
    // on a slow phone/network (measured ~35s worst case on the emulator)
    // and the spinner honestly covers the wait. Pauses are never
    // policy-blocked but CAN be sat on through a buffering stretch, and a
    // too-short leash snapped the UI back to "playing" over the user's
    // pause -- another couple-seconds-of-dead-taps flavor.
    intentDeadlineRef.current = Date.now() + (next ? 20000 : 4000);
    mutedRescueTriedRef.current = false;
    setIsPlaying(next); // optimistic; events + reconciler keep it honest
    setPlayPending(next);
    if (next) onPlay(video.id);
    logTap(next ? "tap:play" : "tap:pause");
    reconcilePlayIntent();
  };

  const handleSeek = (e) => {
    setControlsVisible(true);
    let seekTo = parseFloat(e.target.value);

    // Orientation/intro videos: rewinding is fine, but you can't drag past the
    // furthest point already watched. Snap any forward jump back to that mark.
    if (isOrientation && seekTo > maxReachedRef.current) {
      seekTo = maxReachedRef.current;
    }

    if (isYouTube && playerRef.current) {
      playerRef.current.seekTo(seekTo, true);
    } else if (videoRef.current) {
      videoRef.current.currentTime = seekTo;
    }

    setCurrentTime(seekTo);
    lastTimeRef.current = seekTo;
  };

  // Cycle 1x -> 1.5x -> 2x -> 1x. The speed-sync effect pushes the new rate to
  // the live player.
  const cycleSpeed = (e) => {
    e?.stopPropagation();
    setControlsVisible(true);
    const idx = PLAYBACK_RATES.indexOf(playbackRate);
    setPlaybackRate(PLAYBACK_RATES[(idx + 1) % PLAYBACK_RATES.length]);
  };

  // One CC button opens the caption menu; the menu itself turns captions
  // on (per language) or off. No separate on/off + language buttons.
  const toggleCaptionMenu = (e) => {
    e?.stopPropagation();
    setControlsVisible(true);
    setShowCaptionMenu((s) => !s);
  };

  const chooseCaptionLang = (lang) => {
    setCaptionLang(lang);
    setCaptionsOn(true);
    setShowCaptionMenu(false);
  };

  const captionsOff = () => {
    setCaptionsOn(false);
    setShowCaptionMenu(false);
  };

  const getYouTubeThumbnail = (url) => {
    if (!url) return "/images/default_thumbnail.png";
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = match && match[2].length === 11 ? match[2] : null;

    return videoId
      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      : "/images/default_thumbnail.png";
  };

  // Tapping the video surface reveals the controls (and hides them again
  // while playing).
  //
  // The player surface is full-bleed at the top of the sheet (YouTube-style).
  // In fullscreen it escapes the sheet entirely and fills the viewport via a
  // fixed overlay, so a sideways phone shows edge-to-edge video.
  // NB touch-manipulation here is defensive hygiene only: with a
  // width=device-width viewport Chrome already skips double-tap zoom, so
  // it cannot be (and measurably was not) the half-screen dead-tap cause.
  // The real input-layer differences vs fullscreen are handled at the tap
  // surface (pointer events, no click synthesis); see surfacePointerRef.
  const playerWrapClass = isFullscreen
    ? "fixed inset-0 z-[10000] bg-black overflow-hidden select-none touch-manipulation"
    : "relative w-full bg-black aspect-video overflow-hidden md:rounded-t-[2.5rem] select-none shrink-0 touch-manipulation";

  const modalContent = (
    // On mobile the overlay starts below the h-20 app header so the top bar
    // (menu / title / search / profile) stays visible; the sheet fills the rest
    // of the viewport and scrolls internally (player, title/status/category,
    // then the "Up next" list) with the dashboard never shown behind it.
    // Desktop (md:) keeps the centered dialog, with the overlay covering the
    // WHOLE viewport (md:top-0) -- header and sidebar dimmed behind it. The
    // top offset used to stay at 80px on desktop too (`md:inset-0` loses the
    // `top` battle to `top-20` in the compiled CSS), which shrank the flex
    // container below the max-h-[95vh] dialog: items-center then overflowed
    // it both ways and the top corners clipped off-screen (Marco's 08-10
    // desktop screenshot). md:top-0 targets the same property, so the md
    // variant reliably wins and the dialog centers in the full viewport.
    <div className="fixed inset-x-0 bottom-0 top-20 md:top-0 z-[9999] flex md:items-center md:justify-center md:p-4 bg-black md:bg-black/70">
      <div className="bg-white w-full h-full md:h-auto md:max-w-2xl md:max-h-[95vh] md:rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col">
        {/* --- VIDEO (full-bleed, first) --- */}
        <div ref={playerWrapRef} className={playerWrapClass}>
          {isYouTube ? (
            <div ref={ytContainerRef} className="absolute inset-0" />
          ) : (
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-contain"
              playsInline
              onPlay={() => {
                // Mirror of the YouTube PLAYING branch: settle a reconciler
                // muted-rescue (restore sound while the gesture lives, else
                // show the muted speaker), then let intent rule the UI.
                if (deferredMutedRef.current && videoRef.current) {
                  deferredMutedRef.current = false;
                  if (!isMutedRef.current) {
                    if (gestureStillLive()) videoRef.current.muted = false;
                    else setIsMuted(true);
                  }
                }
                if (desiredPlayingRef.current === null) setIsPlaying(true);
                else reconcilePlayIntent();
                onPlay(video.id);
              }}
              onPause={() => {
                if (desiredPlayingRef.current === null) setIsPlaying(false);
                else reconcilePlayIntent();
              }}
              onEnded={(e) => {
                desiredPlayingRef.current = null;
                setPlayPending(false);
                setIsPlaying(false);
                onProgress(video.id, e.target.duration, 0);
                beginCountdown();
              }}
              onTimeUpdate={(e) => {
                const curr = e.target.currentTime;
                setCurrentTime(curr);
                if (curr > maxReachedRef.current) maxReachedRef.current = curr;
                const delta = curr - lastTimeRef.current;
                const credit = creditedSeconds(delta, e.target.playbackRate, 1);

                if (credit > 0) {
                  onProgress(video.id, curr, credit);
                }

                lastTimeRef.current = curr;
              }}
              onLoadedMetadata={(e) => {
                setDuration(e.target.duration);
                const startAt = initialSavedTimeRef.current;
                if (startAt > 0) {
                  e.target.currentTime = startAt;
                }
              }}
            />
          )}

          {/* Tap surface: the ONE input element for the whole video area
              (the center glyph is visual-only -- a button that unmounts on
              state flips loses the click that needs down+up on one node).
              touch-none, not manipulation: the sheet behind is scrollable,
              and any pan allowance lets Chrome claim a slightly-moving
              touch as a scroll and pointercancel the tap -- the residual
              half-screen dead taps. Fullscreen has no scrollable ancestor,
              which is why it was always clean. If the caption menu is
              open, the first tap just dismisses it. */}
          <div
            className="absolute inset-0 z-10 cursor-pointer touch-none"
            onPointerDown={(e) => {
              // No isPrimary gate: rapid drumming overlaps touches, and
              // dropping the secondary ones read as dead taps. Newest
              // touch wins the ref; its up is the one that counts.
              surfacePointerRef.current = {
                id: e.pointerId,
                x: e.clientX,
                y: e.clientY,
              };
            }}
            onPointerCancel={(e) => {
              logTap("ptrcancel");
              if (surfacePointerRef.current?.id === e.pointerId)
                surfacePointerRef.current = null;
            }}
            onPointerUp={(e) => {
              // Toggle at finger-lift, before click synthesis and its
              // heuristics (see surfacePointerRef). Movement past the slop
              // means a scroll/drag, not a tap.
              const start = surfacePointerRef.current;
              if (!start || start.id !== e.pointerId) return;
              surfacePointerRef.current = null;
              if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 24)
                return;
              lastPointerToggleRef.current = Date.now();
              if (showCaptionMenu) {
                setShowCaptionMenu(false);
                return;
              }
              handleTogglePlay(e);
            }}
            onClick={(e) => {
              // Fallback for click-only environments (tests, some assistive
              // tech). The pointerup path already handled a real tap; its
              // synthetic click follows within a beat and is dropped here.
              if (Date.now() - lastPointerToggleRef.current < 600) return;
              if (showCaptionMenu) {
                setShowCaptionMenu(false);
                return;
              }
              handleTogglePlay(e);
            }}
          />

          {/* Close button (top-right). Same tap-transparency contract as the
              bottom bar: the full-width strip is visual, only the X takes
              pointer events -- taps beside it belong to the video. */}
          <div
            className={`absolute top-0 left-0 right-0 p-3 md:p-4 flex justify-end bg-gradient-to-b from-black/60 to-transparent z-20 transition-opacity duration-300 pointer-events-none ${
              controlsVisible
                ? "opacity-100 [&_button]:pointer-events-auto"
                : "opacity-0"
            }`}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isFullscreen) exitFullscreen();
                onClose();
              }}
              aria-label="Close video"
              className="p-2 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
            >
              <X size={22} />
            </button>
          </div>

          {tapDebugOn && (
            <div
              className="absolute top-2 left-2 z-30 pointer-events-none bg-black/70 text-green-300 text-[10px] font-mono rounded p-1 leading-tight"
              aria-hidden="true"
            >
              {tapLog.length ? (
                tapLog.map((l, i) => <div key={i}>{l}</div>)
              ) : (
                <div>tapdebug on</div>
              )}
            </div>
          )}

          {/* Center control: a spinner until the player can actually take a
              tap, then the play button. Never a tappable play control that
              can't work (see the no-queued-play note up top). */}
          {!isPlayerReady ? (
            <div
              className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 pointer-events-none"
              role="status"
              aria-label="Loading player"
            >
              <div className="w-16 h-16 rounded-full bg-black/50 text-white flex items-center justify-center">
                <Loader2 size={30} className="animate-spin" />
              </div>
              {showLoadingHint && (
                <p className="text-white text-sm bg-black/60 rounded-full px-3 py-1">
                  Loading video&hellip;
                </p>
              )}
            </div>
          ) : playPending ? (
            // A play intent is in flight (cold boots can take seconds on
            // mobile): show buffering, YouTube-style -- never a dead
            // frozen poster. Tappable through to the surface, so a tap
            // here pauses/cancels like on YouTube.
            <div
              className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
              role="status"
              aria-label="Starting playback"
            >
              <div className="w-16 h-16 rounded-full bg-black/50 text-white flex items-center justify-center">
                <Loader2 size={30} className="animate-spin" />
              </div>
            </div>
          ) : (
            !isPlaying &&
            // On touch devices the YouTube MOBILE player paints its own
            // big center play/pause whenever a video pauses after having
            // played -- with controls=0 and right under this overlay, so
            // the paused screen stacks two glyphs at ANY size, not just
            // fullscreen (Android QA 2026-08-10, round 2; desktop embeds
            // never draw it, which is why this reproduces on no desktop).
            // Ours yields exactly there: YT's glyph sits where ours was,
            // and the tap surface + bottom bar still drive playback
            // through our handler. A cued, never-played video shows no
            // YT glyph, so ours stays.
            !(isYouTube && IS_COARSE_POINTER && hasPlayedOnce) && (
              <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                {/* pointer-events-none: VISUAL + keyboard only. Pointer taps
                    fall through to the tap surface, which never unmounts --
                    this button does (on every state flip and the spinner
                    swap), and a tap whose node vanishes between down and up
                    loses its click ("center button doesn't work on some
                    loads", Android QA 08-10 r6). Keyboard activation doesn't
                    hit-test, so Enter/Space still land here. */}
                <button
                  onClick={handleTogglePlay}
                  aria-label="Play"
                  className="pointer-events-none w-16 h-16 rounded-full bg-black/50 text-white flex items-center justify-center transition-colors"
                >
                  <Play size={30} fill="currentColor" className="ml-1" />
                </button>
              </div>
            )
          )}

          {/* Bottom control bar. The CONTAINER is tap-transparent even when
              visible -- only the buttons and the slider take pointer events.
              Its full-width gradient (pt-8 of empty padding + the gaps
              between buttons) covers a big slice of a half-screen video,
              and with the container catching taps every touch there died
              in a handler-less div. Because every successful tap also
              SHOWS the controls for 3s, tap-then-tap-again in the same
              spot hit this zone -- the measured source of the "dead for a
              couple seconds" windows (emulator hit-test, 08-10 r10).
              YouTube's own bar behaves this way: empty-region taps fall
              through to the video. */}
          <div
            className={`absolute bottom-0 left-0 right-0 px-4 pb-4 pt-8 md:px-6 bg-gradient-to-t from-black/90 to-transparent z-20 transition-opacity duration-300 touch-manipulation pointer-events-none ${
              controlsVisible
                ? "opacity-100 [&_button]:pointer-events-auto [&_input]:pointer-events-auto"
                : "opacity-0"
            }`}
          >
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime || 0}
              onChange={handleSeek}
              onClick={(e) => e.stopPropagation()}
              aria-label={isOrientation ? "Seek (rewind only)" : "Seek"}
              className="w-full h-1 accent-orange-500 mb-1"
            />

            {isOrientation && (
              <p className="flex items-center gap-1 text-[11px] text-white/70 mb-2">
                <Lock size={11} />
                You can rewind, but this orientation video can&rsquo;t be
                skipped ahead.
              </p>
            )}

            <div className="flex items-center justify-between text-white text-sm mt-2">
              <div className="flex items-center gap-4 sm:gap-5">
                <button
                  onClick={handleTogglePlay}
                  disabled={!isPlayerReady}
                  aria-label={
                    !isPlayerReady
                      ? "Player loading"
                      : isPlaying
                        ? "Pause"
                        : "Play"
                  }
                >
                  {!isPlayerReady ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : isPlaying ? (
                    <Pause size={20} fill="currentColor" />
                  ) : (
                    <Play size={20} fill="currentColor" />
                  )}
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMuted(!isMuted);
                  }}
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>

                <button
                  onClick={cycleSpeed}
                  aria-label={`Playback speed ${playbackRate}x, tap to change`}
                  className="min-w-[2.5rem] px-1.5 py-0.5 rounded-md text-xs font-bold tabular-nums bg-white/15 hover:bg-white/25 transition-colors"
                >
                  {playbackRate}x
                </button>

                <span className="font-medium tabular-nums">
                  {formatPlaybackTime(currentTime)} /{" "}
                  {formatPlaybackTime(duration)}
                </span>
              </div>

              <div className="flex items-center gap-4 sm:gap-5">
                {/* Captions: one CC button opening the language menu.
                    "No captions available" is shown when the video provably
                    has no tracks (captionStatus === 'none'). */}
                <div className="relative">
                  <button
                    onClick={toggleCaptionMenu}
                    aria-label="Captions"
                    aria-haspopup="menu"
                    aria-expanded={showCaptionMenu}
                    aria-pressed={captionsOn}
                    className={`min-w-[2.5rem] px-1.5 py-0.5 rounded-md text-xs font-bold transition-colors ${
                      captionsOn && captionStatus !== "none"
                        ? "bg-white/15 text-orange-400 hover:bg-white/25"
                        : "bg-white/15 text-white hover:bg-white/25"
                    }`}
                  >
                    CC
                  </button>

                  {showCaptionMenu && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      role="menu"
                      className="absolute bottom-full right-0 mb-2 w-56 max-h-64 overflow-y-auto rounded-xl bg-slate-900/95 backdrop-blur border border-white/10 shadow-2xl py-1 text-sm"
                    >
                      {captionStatus === "none" ? (
                        <p className="px-3 py-2 text-white/70">
                          No captions are available for this video.
                        </p>
                      ) : (
                        (() => {
                          // Off / English / Español only -- the program's two
                          // languages. Other tracks the video carries are
                          // deliberately not listed (UI clutter for learners);
                          // variants like es-419 still satisfy Español via
                          // base-language matching.
                          const items = [
                            {
                              key: "en",
                              label: "English",
                              lang: {code: "en", label: "English"},
                            },
                            {
                              key: "es",
                              label: "Español (Spanish)",
                              lang: {code: "es", label: "Español"},
                            },
                          ];
                          const activeBase = captionsOn
                            ? baseLangOf(captionLang?.code)
                            : null;
                          const rows = [
                            <button
                              key="off"
                              role="menuitemradio"
                              aria-checked={!captionsOn}
                              onClick={captionsOff}
                              className={`w-full text-left px-3 py-2 hover:bg-white/10 transition-colors ${
                                !captionsOn
                                  ? "text-orange-400 font-semibold"
                                  : "text-white"
                              }`}
                            >
                              Off
                            </button>,
                            ...items.map((it) => {
                              const active =
                                captionsOn &&
                                activeBase === baseLangOf(it.lang.code);
                              return (
                                <button
                                  key={it.key}
                                  role="menuitemradio"
                                  aria-checked={active}
                                  onClick={() => chooseCaptionLang(it.lang)}
                                  className={`w-full text-left px-3 py-2 hover:bg-white/10 transition-colors ${
                                    active
                                      ? "text-orange-400 font-semibold"
                                      : "text-white"
                                  }`}
                                >
                                  {it.label}
                                </button>
                              );
                            }),
                          ];
                          return rows;
                        })()
                      )}
                      {captionStatus === "ready" &&
                        captionsOn &&
                        captionLang?.code &&
                        !captionTracks.some(
                          (t) =>
                            baseLangOf(t.code) === baseLangOf(captionLang.code),
                        ) && (
                          <p className="px-3 py-1.5 text-[11px] text-white/50 border-t border-white/10">
                            Translated automatically by YouTube.
                          </p>
                        )}
                      {captionStatus === "unknown" && (
                        <p className="px-3 py-1.5 text-[11px] text-white/50 border-t border-white/10">
                          Checking this video&rsquo;s captions&hellip;
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize size={20} />
                  ) : (
                    <Maximize size={20} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Transient caption notice: appears immediately when the
              no-captions verdict lands after the viewer picked a language. */}
          {captionNotice && (
            <div
              role="status"
              className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-lg bg-black/80 text-white text-sm whitespace-nowrap pointer-events-none"
            >
              {captionNotice}
            </div>
          )}

          {/* Auto-advance countdown: shown over the player when a video ends
              and the playlist has a next video. */}
          {countdown != null && nextVideo && (
            <div className="absolute inset-0 z-30 bg-black/85 flex flex-col items-center justify-center text-center px-6 text-white">
              <p className="text-[11px] font-bold uppercase tracking-wide text-white/60 mb-2">
                Up next
              </p>
              <p className="text-base md:text-lg font-semibold mb-1 line-clamp-2 max-w-md">
                {nextVideo.title}
              </p>
              <p className="text-sm text-white/70 mb-5">
                Playing in {countdown}s
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCountdown(null);
                    if (onSwitchVideo) {
                      autoplayNextRef.current = true;
                      if (canReuse(nextVideo)) reuseRef.current = true;
                      onSwitchVideo(nextVideo);
                    }
                  }}
                  className="px-5 py-2 rounded-full bg-white text-slate-900 font-semibold text-sm hover:bg-white/90 transition-colors"
                >
                  Play now
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCountdown(null);
                  }}
                  className="px-5 py-2 rounded-full bg-white/15 text-white font-semibold text-sm hover:bg-white/25 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* --- INFO + SUGGESTIONS (scrollable) --- */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-5 md:px-8 pt-4 md:pt-6">
            <h2 className="text-base md:text-xl font-bold text-slate-900 mb-1">
              {video.title}
            </h2>
            {channel && (
              <p className="text-slate-700 text-sm font-semibold mb-1.5">
                {channel}
              </p>
            )}
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${classes}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
              {label}
            </span>
            <p className="text-slate-400 text-xs md:text-sm mt-1.5">
              Category:{" "}
              <span className="text-slate-600 font-medium">
                {video.category}
              </span>
            </p>
          </div>

          <div className="px-5 md:px-8 pt-6 pb-8">
            <h4 className="text-lg font-bold text-slate-900 mb-4">
              {fromQueue ? "Up next" : "Suggestions"}
              {suggestions.length > 0 && (
                <span className="ml-2 text-sm font-medium text-slate-400">
                  {suggestions.length}
                </span>
              )}
            </h4>

            {suggestions.length === 0 ? (
              <p className="text-sm text-slate-400">
                You&rsquo;ve reached the end of this playlist.
              </p>
            ) : (
              // Full list; it lives in the panel's own scroll. Thumbnails are
              // lazy <img> so a long playlist doesn't fetch every image at once.
              <div className="space-y-3">
                {suggestions.map((v) => {
                  const vSavedTime = getFurthestPosition(watchHistory[v.id]);
                  const vProgress = getProgressData(vSavedTime, v);
                  const vDuration = Number(v?.videoLength) || 0;
                  const vPct =
                    vDuration > 0
                      ? Math.min(
                          100,
                          Math.max(0, (vSavedTime / vDuration) * 100),
                        )
                      : 0;

                  // Swap the open modal to this video (keeps the playlist
                  // queue via the parent's onSwitchVideo).
                  const handleSwitch = () => {
                    if (onSwitchVideo) onSwitchVideo(v);
                    else onClose?.();
                  };

                  return (
                    <button
                      type="button"
                      key={v.id}
                      onClick={handleSwitch}
                      className="w-full text-left flex items-start gap-3 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-300 rounded-xl p-1 -m-1"
                    >
                      <div className="relative w-32 sm:w-36 aspect-video rounded-lg overflow-hidden bg-slate-200 shrink-0">
                        <img
                          src={getYouTubeThumbnail(v.url)}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                          alt=""
                        />
                        {vPct > 0 && (
                          <span className="absolute bottom-0 left-0 right-0 h-1 bg-white/30">
                            <span
                              className="block h-full bg-orange-500"
                              style={{width: `${vPct}%`}}
                            />
                          </span>
                        )}
                      </div>

                      <div className="min-w-0 flex-1 pt-0.5">
                        <h5 className="font-semibold text-slate-800 text-sm leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {v.title}
                        </h5>
                        <p className="text-slate-400 text-[11px] truncate mt-0.5">
                          {v.category}
                        </p>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 mt-1 rounded-full text-[10px] font-bold border whitespace-nowrap ${vProgress.classes}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${vProgress.dot}`}
                          />
                          {vProgress.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default VideoModal;
